import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QUICK_REPLIES = {
  restaurant: ['How do I add a driver?', 'What are the pricing plans?', 'How do I track my delivery?', 'I need a refund'],
  driver: ['How do I get paid?', 'How do I update my location?', 'I need help with a delivery'],
};

export default function ChatWidget() {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!user || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/chat/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const history = await res.json();
        if (cancelled) return;
        if (Array.isArray(history) && history.length > 0) {
          setMessages(history);
        } else {
          setMessages([{ id: 'welcome', from: 'support', text: `Hey ${user.profile?.firstName || 'there'}! Welcome to Starfuel Support. How can we help you today?`, timestamp: new Date().toISOString() }]);
        }
      } catch (err) {
        if (!cancelled) {
          setMessages([{ id: 'welcome', from: 'support', text: `Hey ${user.profile?.firstName || 'there'}! Welcome to Starfuel Support. How can we help you today?`, timestamp: new Date().toISOString() }]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  useEffect(() => {
    if (isOpen) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  async function sendMessage(text) {
    const msgText = (text || input).trim();
    if (!msgText || sending) return;
    setInput('');
    setSending(true);
    const optimistic = { id: `opt-${Date.now()}`, from: 'user', text: msgText, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    try {
      const res = await fetch(`${API_URL}/api/chat/messages`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ text: msgText }) });
      const data = await res.json();
      setMessages(prev => prev.map(m => m.id === optimistic.id ? data.userMessage : m));
      if (data.autoReply) {
        setIsTyping(true);
        setTimeout(() => { setIsTyping(false); setMessages(prev => [...prev, data.autoReply]); }, 900);
      } else {
        setTimeout(() => { setMessages(prev => [...prev, { id: `ack-${Date.now()}`, from: 'support', text: "Got it! A Starfuel team member will respond shortly during business hours.", timestamp: new Date().toISOString() }]); }, 2000);
      }
    } catch(err) { console.error(err); } finally { setSending(false); }
  }

  function handleKeyDown(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }
  function formatTime(ts) { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }

  if (!user) return null;
  const quickReplies = QUICK_REPLIES[user.role] || [];

  return (
    <>
      {isOpen && (
        <div className="fixed bottom-20 right-4 md:right-6 w-80 md:w-96 flex flex-col rounded-2xl overflow-hidden z-50" style={{ border: '1px solid rgba(230,179,0,0.3)', maxHeight: '520px', background: '#0a0a0a', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }}>
          <div className="flex items-center justify-between px-4 py-3 bg-yellow-400">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-black flex items-center justify-center text-yellow-400 font-black text-sm" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>SF</div>
              <div>
                <p className="text-black font-bold text-sm leading-none">Starfuel Support</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-600 inline-block" />
                  <p className="text-black/70 text-xs">Usually replies in minutes</p>
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="w-7 h-7 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center transition">
              <span className="text-black font-bold text-sm">X</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: '#111111', minHeight: '240px', maxHeight: '320px' }}>
            {messages.map(msg => (
              <div key={msg.id} className={`flex items-end gap-2 ${msg.from === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {msg.from !== 'user' && <div className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center text-black font-black text-xs shrink-0" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>SF</div>}
                <div className={`max-w-xs flex flex-col gap-1 ${msg.from === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`px-3 py-2 text-sm leading-relaxed ${msg.from === 'user' ? 'bg-yellow-400 text-black rounded-2xl rounded-br-sm' : 'bg-gray-800 text-white rounded-2xl rounded-bl-sm'}`}>{msg.text}</div>
                  <span className="text-xs text-gray-600 px-1">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-yellow-400 flex items-center justify-center text-black font-black text-xs shrink-0" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>SF</div>
                <div className="bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                  {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${i*0.15}s` }} />)}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {messages.length <= 2 && quickReplies.length > 0 && (
            <div className="px-3 py-2 flex gap-2 flex-wrap" style={{ background: '#111111', borderTop: '1px solid #2a2a2a' }}>
              {quickReplies.map(qr => (
                <button key={qr} onClick={() => sendMessage(qr)} className="px-3 py-1.5 rounded-full text-xs text-yellow-400 transition" style={{ border: '1px solid rgba(230,179,0,0.4)', background: 'transparent' }}>{qr}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-3" style={{ background: '#1a1a1a', borderTop: '1px solid #2a2a2a' }}>
            <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type a message..." className="flex-1 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-full outline-none placeholder-gray-500" style={{ border: '1px solid #333' }} />
            <button onClick={() => sendMessage()} disabled={!input.trim() || sending} className="w-9 h-9 rounded-full bg-yellow-400 hover:bg-yellow-300 flex items-center justify-center transition-all active:scale-95 disabled:opacity-40 shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
          <div className="text-center py-1.5" style={{ background: '#1a1a1a' }}>
            <p className="text-xs text-gray-600">Powered by <span className="text-yellow-400/60 font-semibold" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</span></p>
          </div>
        </div>
      )}
      <button onClick={() => setIsOpen(o => !o)} className="fixed bottom-4 right-4 md:right-6 w-14 h-14 rounded-full bg-yellow-400 hover:bg-yellow-300 flex items-center justify-center transition-all active:scale-95 z-50" aria-label="Open support chat">
        {unread > 0 && !isOpen && <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{unread}</div>}
        {isOpen ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        )}
      </button>
    </>
  );
}
